import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Card, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { styled } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import * as React from 'react';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { CommunicationIcon, EasyWebinarLogo, EyepasswordIcon, LaunchIcon, ReloadIcon } from '../../assets/icons';
import { addonsImg } from '../../assets/images';
import { config } from '../../config';
import AuthService from '../../services/auth.service';
import ChargebeeService from '../../services/chargebee.service';
import UsageService from '../../services/usage.service';
import { SubscriptionData, PaymentMethodStatusData } from '../../types/chargebee';
import { formatDate, validateUserPermissions } from '../../utils/common.util';
import { STORAGE_KEYS } from '../../utils/storage.util';
import { decodeIdToken, decodeMetadataToken } from '../../utils/token.utils';
import { LeadsquaredService } from '../../services/leadsquared/leadsquared.service';
import Loader from '../common/Loader';
import { GhlService } from '../../services/ghl';
// import { CleverTapService } from '../../utils/clevertap.utils';


interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}


function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}


const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 8,
    borderRadius: 18,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: "#DDE0E5",
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 16,
        backgroundColor: '#07CB7399',
    },
}));

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

export default function ManageSubscription({ navigate }: any) {
    const [value, setValue] = React.useState(0);
    const [subscriptionData, setSubscriptionData] = React.useState<SubscriptionData[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [isLaunchingEasyWebinar, setIsLaunchingEasyWebinar] = useState(false);
    const [liveAttendeesMaxCount, setLiveAttendeesMaxCount] = useState<number>(0);
    const [participantsMaxCount, setParticipantsMaxCount] = useState<number>(0);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [confirmPurchaseModal, setConfirmPurchaseModal] = useState(false);
    const [successModal, setSuccessModal] = useState(false);
    const [isProcessingPurchase, setIsProcessingPurchase] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState<PaymentMethodStatusData | null>(null);


    useEffect(() => {
        // Security: Validate user permissions before allowing access to subscription
        try {
            const permissionCheck = validateUserPermissions('OWNER');
            if (!permissionCheck.isValid) {
                toast.error(permissionCheck.error || 'Access denied: Only account owners can manage subscriptions');
                navigate('/dashboard/overview');
                return;
            }
            setIsAuthorized(true);

            // Track subscription page visit
            // const pageVisitData = {
            //     'Page Title': document.title,
            //     'Page URL': window.location.href,
            //     'UTM Source': localStorage.getItem('utm_source') || '',
            //     'UTM Medium': localStorage.getItem('utm_medium') || '',
            //     'UTM Campaign': localStorage.getItem('utm_campaign') || '',
            //     'UTM Content': localStorage.getItem('utm_content') || '',
            //     'UTM Term': localStorage.getItem('utm_term') || '',
            // };

            // CleverTapService.trackEvent('ew_view_subscription_page', pageVisitData);

            fetchSubscriptionDetails();
        } catch (error) {
            console.error('Error validating subscription permissions:', error);
            toast.error('Error validating permissions');
            navigate('/dashboard/overview');
        }
    }, [navigate]);

    // Fetch payment method status
    useEffect(() => {
        const fetchPaymentStatus = async () => {
            try {
                const metadata = decodeMetadataToken();
                const customerId = metadata?.companies?.[0]?.id;

                if (customerId) {
                    const response = await ChargebeeService.getPaymentMethodStatus(customerId);
                    setPaymentStatus(response.data);
                }
            } catch (error) {
                console.error('Error fetching payment status:', error);
                // Don't show error to user, just log it
            }
        };

        fetchPaymentStatus();
    }, []);

    const fetchSubscriptionDetails = async () => {
        try {
            const metadata = decodeMetadataToken();
            const customerId = metadata?.companies?.[0]?.id
            const entitlement = metadata?.companies?.[0]?.entitlements;
            entitlement?.forEach((el: any) => {
                if (el.featureDescription === 'attendee_registered') {
                    setLiveAttendeesMaxCount(Number(el.value) || 0);
                }
                if (el.featureDescription === 'attendee_joined') {
                    setParticipantsMaxCount(Number(el.value) || 0);
                }
            });
            const response = await ChargebeeService.getSubscriptionDetails(customerId);
            setSubscriptionData(response.data);

            // Check if the plan in API response matches the plan in metadata token
            checkAndRefreshTokenIfNeeded(response.data, metadata);
        } catch (error) {
            console.error('Failed to fetch subscription details:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsageData = async () => {
        try {
            const usageResult = await UsageService.fetchUsage();

            if (usageResult && usageResult.data) {
                let liveAttendeesFound = false;
                let participantsFound = false;

                usageResult.data.forEach((el: any) => {
                    if (el.event_id === "attendee_joined") {
                        setLiveAttendeesMaxCount(Number(el.max_count) || 0);
                        liveAttendeesFound = true;
                    }
                    if (el.event_id === "attendee_registered") {
                        setParticipantsMaxCount(Number(el.max_count) || 0);
                        participantsFound = true;
                    }
                });

                // Set to 0 if not found in response
                if (!liveAttendeesFound) {
                    setLiveAttendeesMaxCount(0);
                }
                if (!participantsFound) {
                    setParticipantsMaxCount(0);
                }
            } else {
                setLiveAttendeesMaxCount(0);
                setParticipantsMaxCount(0);
            }
        } catch (error) {
            console.error('Failed to fetch usage data:', error);
            // Set to 0 on error instead of keeping default 1
            setLiveAttendeesMaxCount(0);
            setParticipantsMaxCount(0);
        }
    };

    const checkAndRefreshTokenIfNeeded = async (apiSubscriptions: SubscriptionData[], metadata: any) => {
        if (!metadata?.companies?.[0]?.subscription) return;

        const metadataPlanId = metadata.companies[0].subscription.planId;

        // Find the active plan subscription from API response
        const activePlanSubscription = apiSubscriptions.find(data =>
            data.subscription.status === 'active' &&
            data.subscription_items.some(item => item.itemType === 'plan')
        );

        if (!activePlanSubscription) return;

        // Get the plan item from the active subscription
        const planItem = activePlanSubscription.subscription_items.find(item => item.itemType === 'plan');

        if (!planItem) return;

        // Extract plan ID from the price ID (assuming format like "Automated-Gold-USD-Monthly")
        const apiPlanId = planItem.itemPriceId;

        // If plans don't match, refresh the token
        if (metadataPlanId !== apiPlanId) {
            console.log('Plan mismatch detected. Refreshing token...');
            try {
                await AuthService.refreshToken();
                console.log('Token refreshed successfully');
            } catch (error) {
                console.error('Failed to refresh token:', error);
            }
        }
    };

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const handleEasyWebinarClick = async () => {
        try {
            // Show loading indicator only for EasyWebinar
            setIsLaunchingEasyWebinar(true);

            // First refresh the token to ensure we have the latest subscription data
            await AuthService.refreshToken();

            // Check subscription status in metadata token
            const metadata = decodeMetadataToken();
            const subscriptionStatus = metadata?.companies?.[0]?.subscription?.status;

            if (subscriptionStatus === 'cancelled') {
                // Show error message if subscription is cancelled
                toast.error('Your subscription has been cancelled. Please renew your subscription to access EasyWebinar.');
                setIsLaunchingEasyWebinar(false);
                return;
            }

            // If subscription is active, proceed with navigation
            const state = localStorage.getItem(STORAGE_KEYS.STATE);
            const easyWebinarUrl = `${config.easywebinar_v2_url}/ew-auto-login${state ? `?state=${state}` : ''}`;
            window.location.href = easyWebinarUrl;
        } catch (error) {
            console.error('Error navigating to EasyWebinar:', error);
            toast.error('An error occurred. Please try again later.');
            setIsLaunchingEasyWebinar(false);
        }
    };


    const handleUpdatePayment = async () => {
        try {
            await ChargebeeService.openPaymentSection(async () => {
                // Refresh payment status after payment method is updated
                const metadata = decodeMetadataToken();
                const customerId = metadata?.companies?.[0]?.id;

                if (customerId) {
                    const response = await ChargebeeService.getPaymentMethodStatus(customerId);
                    setPaymentStatus(response.data);
                }
            });
        } catch (error) {
            console.error('Error opening payment section:', error);
            toast.error('Failed to open payment section. Please try again.');
        }
    };

    const handleBookBannerClick = () => {
        // Check if there are payment issues
        if (paymentStatus) {
            const hasPaymentIssues = paymentStatus.hasExpiredCard ||
                paymentStatus.hasInvalidCard ||
                paymentStatus.hasExpiringCard ||
                !paymentStatus.hasPaymentMethod;

            if (hasPaymentIssues) {
                // Open payment method modal instead of purchase confirmation
                handleUpdatePayment();
                return;
            }
        }

        // If no payment issues, proceed with purchase confirmation
        setConfirmPurchaseModal(true);
    };

    const handleConfirmPurchase = async () => {
        const metadata = decodeMetadataToken();
        console.log("metadata", metadata);
        try {
            setIsProcessingPurchase(true);
            setConfirmPurchaseModal(false);

            const paymentData = await ChargebeeService.quickCharge(
                metadata?.companies?.[0]?.id ?? "",
                "Digital-Book-USD",
                1
            );

            if (paymentData && paymentData.status === "paid") {
                setSuccessModal(true);
                setIsProcessingPurchase(false);

                // Track purchase in Leadsquared
                try {
                    const idToken = decodeIdToken();
                    const email = idToken?.email;
                    console.log("idToken", idToken);
                    console.log("email", email);
                    if (email) {
                        const ghlContactId = await GhlService.getContactByEmail(email as string);
                        if (ghlContactId) {
                            await GhlService.addTags(ghlContactId.id, ["Sales Experience"]);
                        }
                        const LsData = await LeadsquaredService.getLeadsByEmail(email as string);
                        const LsId = (LsData as any).ProspectID;
                        if (LsId) {
                            await LeadsquaredService.joinActivity({
                                id: LsId,
                                activityEvent: 205,
                                activityValue: "Sales Experience",
                                activityNote: "Digital Book Purchased - Payment Succeeded ($7.00)"
                            });
                            console.log('Leadsquared activity tracked successfully');
                        } else {
                            console.warn('Lead not found in Leadsquared for email:', email, 'Response:', LsData);
                        }
                    }
                } catch (leadsquaredError) {
                    console.error('Error tracking Leadsquared activity:', leadsquaredError);
                    // Don't fail the whole flow if Leadsquared tracking fails
                }

            } else {
                console.error("Failed to initiate checkout. Please try again.");
                toast.error("Failed to initiate checkout. Please try again.");
                setIsProcessingPurchase(false);
            }

        } catch (error) {
            console.error("Error purchasing book:", error);
            toast.error("An error occurred. Please try again later.");
            setIsProcessingPurchase(false);
        }
    };

    const handleCancelPurchase = () => {
        setConfirmPurchaseModal(false);
    };

    const renderSubscriptionCards = () => {
        return subscriptionData.map((data) => {
            const planItem = data.subscription_items.find(item => item.itemType === 'plan');
            const addonItems = data.subscription_items.filter(item => item.itemType === 'addon');

            return (
                <React.Fragment key={data.subscription.id}>
                    {/* Plan Card */}
                    {planItem && (
                        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                            <Card sx={{ padding: '32px 23px', border: '1px solid #E8EAF0', borderRadius: '8px', boxShadow: '0px 4px 12px 0px rgba(43, 43, 48, 0.06)', display: 'flex', flexDirection: 'column !important', justifyContent: 'center', height: '100%' }}>
                                <Box display='flex' justifyContent='space-between' flexDirection='column' height='100%'>
                                    <Box textAlign='center'>
                                        <EasyWebinarLogo />
                                        <Box textAlign='center' mt={{ xs: '20px', md: '50px' }} mb='44px'>
                                            <Typography fontWeight='500' color='#333333' fontSize={{ xs: '20px', sm: '24px' }} mb='12px'>
                                                {planItem.itemName}
                                            </Typography>
                                            {data.subscription?.status === 'active' && (
                                                <Typography color='#333333' fontSize='16px' mb='12px'>
                                                    Renews on {formatDate(data.subscription?.currentTermEnd)}
                                                </Typography>
                                            )}
                                            {data.subscription?.status === 'in_trial' && !planItem.itemPriceId.includes("free-trial-plan") && (
                                                <Typography color='#333333' fontSize='16px' mb='12px'>
                                                    Trial ends on {formatDate(data.subscription?.planExpireDate)}
                                                </Typography>
                                            )}
                                            <Typography color='#666666' fontSize='16px'>
                                                {planItem.itemPriceId.includes("free-trial-plan") ? "10" : participantsMaxCount + liveAttendeesMaxCount} Attendees
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Box
                                            onClick={handleEasyWebinarClick}
                                            color='#178EFC'
                                            borderRight='8px'
                                            mb='12px'
                                            fontSize='16px'
                                            display='flex'
                                            alignItems='center'
                                            justifyContent='center'
                                            gap='10px'
                                            p='8px'
                                            sx={{
                                                '&:hover': {
                                                    bgcolor: 'rgb(23 142 252 / 7%)',
                                                    borderRadius: '8px'
                                                },
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {isLaunchingEasyWebinar ? <Loader sx={{ position: 'fixed', zIndex: 999999, top: 0, right: 0, left: 0, bottom: 0 }} fullHeight minHeight='100vh' text='Launching EasyWebinar...' fontSize='16px' /> : 'Launch EasyWebinar'} <LaunchIcon />
                                        </Box>
                                        <Button
                                            size='large'
                                            className='viewDetail-button-wrapper'
                                            variant='outlined'
                                            fullWidth
                                            onClick={() => navigate('/subscription/details', {
                                                state: { subscriptionData: data }
                                            })}
                                            sx={{
                                                textTransform: 'capitalize',
                                                color: '#333333',
                                                border: '1px solid #666666',
                                                borderRadius: '8px',
                                                '&:hover': {
                                                    backgroundColor: '#178EFC',
                                                    color: '#FFFFFF',
                                                    borderColor: 'transparent',
                                                }
                                            }}
                                        >
                                            View details / upgrade
                                            <Box sx={{ display: 'flex', marginLeft: '10px' }}>
                                                <EyepasswordIcon />
                                            </Box>
                                        </Button>
                                    </Box>
                                </Box>
                            </Card>
                        </Grid>
                    )}

                    {/* Add-on Cards */}
                    {addonItems.map((addon) => (
                        <Grid key={addon.itemPriceId} size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                            <Card sx={{ padding: '32px 23px', border: '1px solid #E8EAF0', boxShadow: '0px 4px 12px 0px rgba(43, 43, 48, 0.08)', display: 'flex', flexDirection: 'column !important', justifyContent: 'center', position: 'relative', height: '100%', borderRadius: '8px' }}>
                                <Box display='flex' justifyContent='space-between' flexDirection='column' height='100%'>
                                    <Box textAlign='center'>
                                        <Box>
                                            <CommunicationIcon />

                                            <Box position='absolute' right='0' top='0'>
                                                <img src={addonsImg} />
                                            </Box>
                                        </Box>
                                        <Box textAlign='center' mt='34px' mb='61px'>
                                            <Typography fontWeight='500' color='#333333' fontSize={{ xs: '20px', sm: '24px' }}>
                                                {addon.itemName}
                                            </Typography>
                                            <Typography color='#333333' fontSize='16px' mb='12px'>
                                                Renews on {formatDate(data.subscription.nextBillingAt)}
                                            </Typography>
                                        </Box>
                                        <Typography color='#22B060' fontSize='34px' fontWeight='500' display='flex' justifyContent='center' alignItems='baseline'>
                                            ${(addon.amount / 100).toFixed(2)}/ <Typography fontSize='18px'>month</Typography>
                                        </Typography>
                                    </Box>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </React.Fragment>
            );
        });
    };



    // Security: Don't render component if user is not authorized
    if (!isAuthorized) {
        return <Loader fullHeight minHeight="80vh" />;
    }

    return (
        <Container sx={{ maxWidth: "1200px", px: '0!important' }}>
            {loading ? (
                <Loader fullHeight minHeight="80vh" />
            ) : (

                <Box className='tabs-modification'>
                    {/* Book Banner - Landscape (10:1 ratio) */}
                    <Box
                        onClick={handleBookBannerClick}
                        sx={{
                            borderRadius: '8px',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            mb: { xs: "24px", sm: '28px', xl: "34px" },
                            transition: 'transform 0.3s ease-in-out',
                            boxShadow: '0px 4px 12px 0px rgba(43, 43, 48, 0.09)',
                            '& img': {
                                display: 'block',
                                maxWidth: '100%',
                                willChange: 'transform',
                            },
                            '&:hover': {
                                transform: 'translateY(-3px)',
                            }
                        }}
                    >
                        <img
                            src="/images/bookBanner2.png"
                            alt="Book Banner"
                        />
                    </Box>
                    {/* <Box 
                        sx={{ 
                        width: '100%', 
                        mt: 1, 
                        mb: 1, 
                        display: 'flex', 
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease-in-out',
                        '&:hover': {
                            transform: 'translateY(-2px)'
                        }
                        }}
                        onClick={() => {
                        // Find the active subscription with a plan item
                        const activeSubscription = subscriptionData.find((data) => {
                            const planItem = data.subscription_items.find(item => item.itemType === 'plan');
                            return planItem && (data.subscription.status === 'active' || data.subscription.status === 'in_trial');
                        });
                        
                        if (activeSubscription) {
                            navigate('/subscription/details', { 
                                state: { 
                                    subscriptionData: activeSubscription,
                                    scrollToPricing: true 
                                } 
                            });
                        } else {
                            // Fallback: navigate without subscription data if no active subscription found
                            navigate('/subscription/details', { 
                                state: { scrollToPricing: true } 
                            });
                        }
                        }}
                    >
                        <img
                            src={
                                "https://easywebinar-site.easywebinar.com/images/all-pages-banner.png"
                            }
                            alt="Sale Banner"
                            fetchPriority="high"
                            style={{ width: '100%', maxWidth: 1600, borderRadius: 12, boxShadow: '0px 4px 24px 0px #2B2B3040' }}
                        />
                    </Box> */}
                    <Box mb={{ xs: "34px", sm: '20px', xl: "34px" }}>
                        <Typography color="#333333" fontSize="24px" mb="15px" fontWeight="500">
                            Subscriptions
                        </Typography>
                        <Typography color='#333333' fontSize='16px'>Manage your subscriptions</Typography>
                    </Box>
                    <Box sx={{ width: '100%' }}>
                        <Box display='flex' justifyContent='space-between' alignItems='center' flexWrap={{ xs: 'wrap', sm: 'unset' }}>
                            <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons="auto">
                                <Tab label="Active" {...a11yProps(0)} sx={{ textTransform: 'capitalize', fontSize: { xs: '14px', sm: '16px' }, padding: { xs: '6px', sm: 'unset' } }} />
                                <Tab label="Expired" {...a11yProps(1)} sx={{ textTransform: 'capitalize', fontSize: { xs: '14px', sm: '16px' }, padding: { xs: '6px', sm: 'unset' } }} />
                            </Tabs>
                            {/* <Box display='flex' alignItems='center' mt={{ xs: '15px', sm: 'unset' }} width={{ xs: '100%', sm: 'unset' }} justifyContent={{ xs: 'flex-end', sm: 'unset' }}>
                                <Typography color='#333333' fontSize='14px' mr='8px' >Manage Notifications</Typography>
                                <Box onClick={handleDialogOpen}
                                    sx={{
                                        cursor: "pointer",
                                        height: "36px",
                                        width: "36px",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        // padding:"5px",
                                        "&:hover": {
                                            border: "1px solid #178EFC",
                                            borderRadius: "4px",
                                            "& svg": {
                                                "& path": {
                                                    fill: "#178EFC",
                                                }

                                            },
                                        },
                                    }}
                                ><NotificationIcon ></NotificationIcon></Box>
                                <ManageNotification
                                    onClose={handleDialogClose}
                                    open={dialogOpen}
                                />
                            </Box> */}
                        </Box>
                        <CustomTabPanel value={value} index={0}>
                            <Grid container rowSpacing={{ xs: 3, sm: 3, md: 3 }} columnSpacing={{ xs: 3, sm: 2, md: 3 }}>
                                {renderSubscriptionCards()}
                            </Grid>
                            {/* <Grid container rowSpacing={{ xs: 3, sm: 3, md: 3 }} columnSpacing={{ xs: 3, sm: 2, md: 3 }}>
                                <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                                    <Typography color='#333333' fontWeight='500' fontSize='24px' mb='20px' mt={{ xs: '30px', lg: '77px' }}>Explore Other Products</Typography>
                                    <Box sx={{ padding: { xs: '23px', lg: '32px' }, boxShadow: '0px 4px 12px 0px rgba(43, 43, 48, 0.08)', bgcolor: '#fff', border: '1px solid #614EFA', borderRadius: '8px' }}
                                    >
                                        <Box>
                                            <EasyCrmLogo />
                                            <Typography mb='12px' fontSize='16px' color='#333333' mt='24px'>Improve Your Sales Process & Grow Your Business</Typography>
                                            <Typography mb='20px' fontSize='14px' color='#666666'>EasySuite CRM helps make selling easier! Track your leads from start to finish, identify successful sales strategies, collaborate with your team and access everything from one easy to use platform.</Typography>
                                        </Box>
                                        <Box display='flex' gap='17px'>
                                            <Button variant='outlined' onClick={handleButtonClick} size='large' sx={{
                                                textTransform: 'capitalize', border: '1px solid #614EFA', borderRadius: '8px', color: '#614EFA', "&:hover": {
                                                    border: "1px solid #614EFA",
                                                }
                                            }}>Start Trial</Button>
                                        </Box>
                                    </Box>
                                    <Box>
                                    </Box>
                                </Grid>
                            </Grid> */}
                        </CustomTabPanel>
                        <CustomTabPanel value={value} index={1}>
                            <Typography color='#333333' fontWeight='500' fontSize='18px' mb='12px'>Plans</Typography>
                            {subscriptionData.some(data => data.subscription.deleted || data.subscription.status === 'cancelled') ? (
                                <Grid container rowSpacing={{ xs: 3, sm: 3, md: 3 }} columnSpacing={{ xs: 3, sm: 2, md: 3 }}>
                                    {subscriptionData
                                        .filter(data => data.subscription.deleted || data.subscription.status === 'cancelled')
                                        .map((data) => {
                                            const planItem = data.subscription_items.find(item => item.itemType === 'plan');
                                            const addonItems = data.subscription_items.filter(item => item.itemType === 'addon');

                                            return (
                                                <React.Fragment key={data.subscription.id}>
                                                    {/* Expired Plan Card */}
                                                    {planItem && (
                                                        <Grid size={{ xs: 12, sm: 6, md: 6, xl: 4 }}>
                                                            <Card sx={{ padding: '32px 23px', border: '1px solid #E8EAF0', boxShadow: '0px 4px 12px 0px rgba(43, 43, 48, 0.08)', display: 'flex', flexDirection: 'column !important', justifyContent: 'center', height: '100%', borderRadius: '8px' }}>
                                                                <Box display='flex' justifyContent='space-between' flexDirection='column' height='100%'>
                                                                    <Box textAlign='center'>
                                                                        <EasyWebinarLogo />
                                                                        <Box textAlign='center' mt={{ xs: '20px', md: '50px' }} mb={{ xs: '20px', sm: '59px' }}>
                                                                            <Typography fontWeight='500' color='#333333' fontSize={{ xs: '20px', sm: '24px' }} mb='12px'>
                                                                                {planItem.itemName}
                                                                            </Typography>
                                                                            <Typography color='#666666' fontSize='16px' mb='12px'>
                                                                                {participantsMaxCount + liveAttendeesMaxCount} participants
                                                                            </Typography>
                                                                            <Typography color='#E44258' fontSize='16px' mb='12px'>
                                                                                Expired on {formatDate(data.subscription.planExpireDate)}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                    <Button
                                                                        size='large'
                                                                        className='renew-button-wrapper'
                                                                        variant='outlined'
                                                                        fullWidth
                                                                        sx={{
                                                                            textTransform: 'capitalize',
                                                                            color: '#333333',
                                                                            border: '1px solid #666666',
                                                                            borderRadius: '8px',
                                                                            '&:hover': {
                                                                                backgroundColor: '#178EFC',
                                                                                color: '#FFFFFF',
                                                                                border: 'none'
                                                                            }
                                                                        }}
                                                                    >
                                                                        Renew Now
                                                                        <Box sx={{ display: 'flex', marginLeft: '10px' }}>
                                                                            <ReloadIcon />
                                                                        </Box>
                                                                    </Button>
                                                                </Box>
                                                            </Card>
                                                        </Grid>
                                                    )}

                                                    {/* Expired Add-on Cards */}
                                                    {addonItems.map((addon) => (
                                                        <Grid key={addon.itemPriceId} size={{ xs: 12, sm: 6, md: 6, xl: 4 }}>
                                                            <Card sx={{ padding: '32px 23px', border: '1px solid #E8EAF0', boxShadow: '0px 4px 12px 0px rgba(43, 43, 48, 0.08)', display: 'flex', flexDirection: 'column !important', justifyContent: 'center', position: 'relative', height: '100%', borderRadius: '8px' }}>
                                                                <Box display='flex' justifyContent='space-between' flexDirection='column' height='100%'>
                                                                    <Box textAlign='center'>
                                                                        <Button sx={{
                                                                            position: 'absolute',
                                                                            right: '0',
                                                                            borderRadius: "4px 0 0 4px",
                                                                            color: '#fff !important',
                                                                            fontSize: '16px',
                                                                            textTransform: 'capitalize',
                                                                            fontWeight: '500',
                                                                            px: '25px',
                                                                            height: '30px',
                                                                            bgcolor: '#A3A3A3',
                                                                            ':hover': {
                                                                                boxShadow: 'none !important',
                                                                                cursor: 'unset',
                                                                            }
                                                                        }}>Add Ons</Button>
                                                                        <Box textAlign='center' mt='34px' mb={{ xs: '20px', sm: '59px' }}>
                                                                            <CommunicationIcon></CommunicationIcon>
                                                                            <Typography fontWeight='500' color='#333333' fontSize={{ xs: '20px', sm: '24px' }} mb='12px'>Additional Co Hosts</Typography>
                                                                            <Typography color='#333333' fontSize='16px' mb='12px'>1 additional Co Host</Typography>
                                                                            <Typography color='#E44258' fontSize='16px' mb='12px'>Expired on Jun 11</Typography>
                                                                        </Box>
                                                                        <Button size='large' className='renew-button-wrapper' variant='outlined' fullWidth sx={{
                                                                            textTransform: 'capitalize', color: '#333333', border: '1px solid #666666', borderRadius: '8px', '&:hover': {
                                                                                backgroundColor: '#178EFC',
                                                                                color: '#FFFFFF',
                                                                                border: 'none'
                                                                            }
                                                                        }}>Renew Now<Box sx={{ display: 'flex', marginLeft: '10px' }}><ReloadIcon /></Box>
                                                                        </Button>
                                                                    </Box>
                                                                </Box>
                                                            </Card>
                                                        </Grid>
                                                    ))}
                                                </React.Fragment>
                                            );
                                        })}
                                </Grid>
                            ) : (
                                <Typography color='#666666' fontSize='16px'>
                                    No expired plans found
                                </Typography>
                            )}
                        </CustomTabPanel>
                    </Box >
                </Box>
            )}

            {/* Confirmation Purchase Modal */}
            <Dialog
                open={confirmPurchaseModal}
                onClose={handleCancelPurchase}
                sx={{
                    "& .MuiDialog-paper": {
                        maxWidth: "500px",
                        width: "100%",
                        borderRadius: "16px",
                        padding: "24px",
                    },
                }}
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 0 16px 0" }}>
                    <Typography fontSize="20px" fontWeight="600" color="#333333">
                        Confirm Purchase
                    </Typography>
                    <IconButton
                        onClick={handleCancelPurchase}
                        size="small"
                        sx={{ color: "#666666" }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ padding: "0 0 24px 0" }}>
                    <Typography fontSize="16px" color="#666666" lineHeight="1.6">
                        Are you sure you want to purchase this book? <strong>$7.00</strong> will be charged from your payment method.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: "0", gap: "12px", justifyContent: "flex-end" }}>
                    <Button
                        variant="outlined"
                        onClick={handleCancelPurchase}
                        sx={{
                            textTransform: "capitalize",
                            borderRadius: "8px",
                            height: "44px",
                            minWidth: "100px",
                            borderColor: "#666666",
                            color: "#333333",
                            "&:hover": {
                                borderColor: "#666666",
                                backgroundColor: "rgba(102, 102, 102, 0.04)",
                            },
                        }}
                        disabled={isProcessingPurchase}
                    >
                        No
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmPurchase}
                        sx={{
                            textTransform: "capitalize",
                            borderRadius: "8px",
                            height: "44px",
                            minWidth: "100px",
                            backgroundColor: "#178EFC",
                            "&:hover": {
                                backgroundColor: "#1578D9",
                            },
                        }}
                        disabled={isProcessingPurchase}
                    >
                        {isProcessingPurchase ? (
                            <>
                                <CircularProgress size={20} sx={{ color: "#fff", mr: 1 }} />
                                Processing...
                            </>
                        ) : (
                            "Yes"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Modal */}
            <Dialog
                open={successModal}
                onClose={() => {
                    setSuccessModal(false);
                }}
                sx={{
                    "& .MuiDialog-paper": {
                        maxWidth: "500px",
                        width: "100%",
                        borderRadius: "16px",
                        padding: "24px",
                    },
                }}
            >
                <DialogContent sx={{ padding: "24px 0", textAlign: "center" }}>
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                        <CheckCircleIcon sx={{ fontSize: 64, color: "#22B060" }} />
                    </Box>
                    <Typography fontSize="20px" fontWeight="600" color="#333333" mb={1}>
                        Purchase Initiated Successfully!
                    </Typography>
                    <Typography fontSize="16px" color="#666666" lineHeight="1.6">
                        Your book purchase has been processed. Click OK to complete the payment.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: "0", justifyContent: "center" }}>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setSuccessModal(false);
                        }}
                        sx={{
                            textTransform: "capitalize",
                            borderRadius: "8px",
                            height: "44px",
                            minWidth: "120px",
                            backgroundColor: "#178EFC",
                            "&:hover": {
                                backgroundColor: "#1578D9",
                            },
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Container >
    );
}
